import type { TooltipProps } from "@mui/material";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import type { CSSProperties, KeyboardEventHandler, ReactNode } from "react";
import { styled } from "@mui/material/styles";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 13,
  },
}));

interface Props<T extends string> {
  readonly label: string;
  readonly helperText?: ReactNode;
  readonly onHelperDoubleClick?: () => void;
  readonly value: T;
  readonly values: readonly { readonly value: T; readonly label?: string; readonly tooltip?: string }[];
  readonly disabled?: boolean;
  readonly readonly?: boolean;
  readonly size?: "small" | "medium";
  readonly style?: CSSProperties;
  readonly fullWidth?: boolean;
  readonly error?: boolean;
  readonly onSelect: (id: T) => void;
  readonly onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export const Select = <T extends string = string>({
  label,
  helperText,
  onHelperDoubleClick,
  value,
  values,
  disabled,
  readonly,
  size,
  style,
  fullWidth,
  error,
  onSelect,
  onKeyDown,
}: Props<T>) => {
  const selectedValue = values.find((v) => v.value === value);
  const labelId = `select-named-label-${label?.replace(" ", "-") ?? "id"}`;
  return (
    <FormControl variant="outlined" fullWidth={fullWidth} size={size} style={style}>
      <InputLabel id={labelId} disabled={disabled}>
        {label}
      </InputLabel>
      <MuiSelect
        labelId={labelId}
        label={label}
        size={size}
        disabled={disabled}
        inputProps={{ readOnly: readonly }}
        error={error}
        value={selectedValue?.value ?? ""}
        fullWidth={fullWidth}
        onChange={(e) => onSelect(e.target.value as T)}
        onKeyDown={onKeyDown}
      >
        {values.map((v) => {
          const text = v.label ?? v.value;
          if (v.tooltip) {
            return (
              <MenuItem key={v.value} value={v.value}>
                <LightTooltip key={v.value} title={v.tooltip} placement="right">
                  <div style={{ width: "100%" }}>{text}</div>
                </LightTooltip>
              </MenuItem>
            );
          }
          return (
            <MenuItem key={v.value} value={v.value}>
              {text}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {helperText && (
        <FormHelperText data-testid="select-hint">
          <span
            onDoubleClick={onHelperDoubleClick}
            style={{ cursor: onHelperDoubleClick ? "context-menu" : "default" }}
          >
            {helperText}
          </span>
        </FormHelperText>
      )}
    </FormControl>
  );
};
